// src/Components/partners/PendingApplicationsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, Mail, Phone, Globe } from 'lucide-react';
import { partnersAPI } from '../../services/api';
import { Skeleton, EmptyState } from '../dashboard/design-system';
import PartnerFormModal from './PartnerFormModal';

export default function PendingApplicationsTab() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [actioningId, setActioningId]   = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null); // application being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [approveTarget, setApproveTarget] = useState(null); // application being approved -> opens form

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnersAPI.listApplications({ status: 'pending' });
      if (res.success) setApplications(res.data.applications || []);
    } catch (err) {
      console.error('Failed to load applications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const handleApproveSubmit = async (form) => {
    setActioningId(approveTarget.id);
    try {
      await partnersAPI.approveApplication(approveTarget.id, form);
      setApproveTarget(null);
      await fetchApplications();
    } catch (err) {
      alert(`Approve failed: ${err}`);
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async () => {
    setActioningId(rejectTarget.id);
    try {
      await partnersAPI.rejectApplication(rejectTarget.id, { rejection_reason: rejectReason });
      setRejectTarget(null);
      setRejectReason('');
      await fetchApplications();
    } catch (err) {
      alert(`Reject failed: ${err}`);
    } finally {
      setActioningId(null);
    }
  };

  const timeAgo = (ts) => {
    if (!ts) return 'Unknown';
    const h = Math.floor((Date.now() - new Date(ts)) / 3600000);
    if (h < 1) return 'Just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
      {loading ? (
        <div className="space-y-3">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}</div>
      ) : applications.length === 0 ? (
        <EmptyState icon={CheckCircle} title="No pending applications" sub="New partner requests from the mobile app show up here" />
      ) : (
        <div className="space-y-3">
          {applications.map((app) => (
            <div key={app.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{app.organization_name}</h4>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock size={11} /> {timeAgo(app.submitted_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{app.contact_name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Mail size={11} /> {app.work_email}</span>
                    {app.phone_number && <span className="flex items-center gap-1"><Phone size={11} /> {app.phone_number}</span>}
                    {app.website_url && <span className="flex items-center gap-1"><Globe size={11} /> {app.website_url}</span>}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {app.country || 'Unknown country'} · {app.industry || 'Unknown industry'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={actioningId === app.id}
                    onClick={() => setApproveTarget(app)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition disabled:opacity-50">
                    <CheckCircle size={13} /> Approve
                  </button>
                  <button
                    disabled={actioningId === app.id}
                    onClick={() => setRejectTarget(app)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition disabled:opacity-50">
                    <XCircle size={13} /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approve -> pre-filled create-partner form */}
      {approveTarget && (
        <PartnerFormModal
          title={`Approve "${approveTarget.organization_name}" as a partner`}
          prefill={{ name: approveTarget.organization_name, country: approveTarget.country, description: '' }}
          submitting={actioningId === approveTarget.id}
          onClose={() => setApproveTarget(null)}
          onSubmit={handleApproveSubmit}
        />
      )}

      {/* Reject reason modal */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Reject "{rejectTarget.organization_name}"?</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional reason (not yet emailed to applicant — see notes)"
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-sm mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setRejectTarget(null); setRejectReason(''); }}
                className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition">
                Cancel
              </button>
              <button onClick={handleReject} disabled={actioningId === rejectTarget.id}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50">
                {actioningId === rejectTarget.id ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}